'use client'

import { createContext, PropsWithChildren, use, useContext } from 'react'

import { PaginatedData, RoomSearchFacetsResponse, SearchAccount } from '@/services/cms'
import { FollowStatusResponse } from '@/services/community'
import { RoomTagWithCount } from '@/types/genre'

type SearchPageContextType = {
  gamesTotal: number
  gamesTags: RoomTagWithCount[]
  experiencesTotal: number
  experiencesTags: RoomTagWithCount[]
  videosTotal: number
  videosTags: RoomTagWithCount[]
  accounts: SearchAccount[] | undefined
  accountsTotal: number
  followStatus: FollowStatusResponse['data'] | undefined
  otherSearchedFor: string[] | undefined
}

const SearchPageContext = createContext<SearchPageContextType | null>(null)

type SearchPageProviderProps = PropsWithChildren<{
  searchGamesTotalPromise: Promise<RoomSearchFacetsResponse | undefined>
  searchExperiencesTotalPromise: Promise<RoomSearchFacetsResponse | undefined>
  searchCreatorsPromise: Promise<PaginatedData<SearchAccount> | undefined>
  searchVideosTotalPromise: Promise<RoomSearchFacetsResponse | undefined>
  followStatusPromise: Promise<FollowStatusResponse | undefined>
  otherSearchedForPromise: Promise<string[] | undefined>
}>

export function SearchPageProvider({
  children,
  searchGamesTotalPromise,
  searchExperiencesTotalPromise,
  searchCreatorsPromise,
  searchVideosTotalPromise,
  followStatusPromise,
  otherSearchedForPromise,
}: SearchPageProviderProps) {
  const searchGamesTotal = use(searchGamesTotalPromise)
  const searchExperiencesTotal = use(searchExperiencesTotalPromise)
  const searchCreators = use(searchCreatorsPromise)
  const searchVideosTotal = use(searchVideosTotalPromise)
  const followStatus = use(followStatusPromise)
  const otherSearchedFor = use(otherSearchedForPromise)

  return (
    <SearchPageContext.Provider
      value={{
        gamesTotal: searchGamesTotal?.total_count || 0,
        gamesTags: searchGamesTotal?.genres || [],
        experiencesTotal: searchExperiencesTotal?.total_count || 0,
        experiencesTags: searchExperiencesTotal?.genres || [],
        videosTotal: searchVideosTotal?.total_count || 0,
        videosTags: searchVideosTotal?.genres || [],
        accounts: searchCreators?.results,
        accountsTotal: searchCreators?.total || 0,
        followStatus: followStatus?.data,
        otherSearchedFor,
      }}
    >
      {children}
    </SearchPageContext.Provider>
  )
}

export const useSearchPageContext = () => {
  const context = useContext(SearchPageContext)

  if (!context) {
    throw new Error('useSearchPageContext must be used within a SearchPageProvider')
  }

  return context
}
